// ─── Preset Picker ────────────────────────────────────────────
// Replaces the simple "Add Element" dropdown with a preset browser.
// Two tabs: System presets (pre-styled) and My Presets (user-saved).

import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus, X, Image as ImageIcon, FileText, Type, Heading,
  Bookmark, Trash2, Star
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { SYSTEM_PRESETS } from '../../lib/presets/collagePresets';

const API = process.env.REACT_APP_BACKEND_URL;

const TYPE_ICONS = {
  image_card: ImageIcon,
  text_note: FileText,
  text_block: Type,
  title_block: Heading,
};

const TYPE_COLORS = {
  image_card: 'text-blue-500 bg-blue-50 dark:bg-blue-900/20',
  text_note: 'text-amber-500 bg-amber-50 dark:bg-amber-900/20',
  text_block: 'text-emerald-500 bg-emerald-50 dark:bg-emerald-900/20',
  title_block: 'text-violet-500 bg-violet-50 dark:bg-violet-900/20',
};

function PresetCard({ preset, onSelect, onDelete, isUser }) {
  const Icon = TYPE_ICONS[preset.type || preset.element?.type] || Type;
  const colorClass = TYPE_COLORS[preset.type || preset.element?.type] || 'text-gray-500 bg-gray-50';
  const name = preset.name;
  const desc = preset.description;

  return (
    <button
      onClick={() => onSelect(isUser ? preset.element : preset)}
      className="w-full group relative flex items-start gap-3 p-3 rounded-xl border border-gray-200 dark:border-gray-800 hover:border-blue-400 dark:hover:border-blue-500 bg-white dark:bg-gray-900 transition-all text-left"
      data-testid={`preset-${(name || '').replace(/\s+/g, '-').toLowerCase()}`}
    >
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${colorClass}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-xs font-medium text-gray-800 dark:text-gray-200 truncate">
          {name}
        </div>
        {desc && (
          <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2">
            {desc}
          </div>
        )}
      </div>
      {isUser && onDelete && (
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(preset.id); }}
          className="absolute top-2 right-2 p-1 rounded-md opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all"
          data-testid={`delete-preset-${preset.id}`}
        >
          <Trash2 className="w-3 h-3" />
        </button>
      )}
    </button>
  );
}

export default function PresetPicker({ sectionId, onAdd, token }) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('system');
  const [userPresets, setUserPresets] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchUserPresets = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/user/element-presets`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUserPresets(res.data);
    } catch {
      // Silently fail — user just won't see their presets
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (open && tab === 'mine') fetchUserPresets();
  }, [open, tab, fetchUserPresets]);

  const handleSelect = (presetData) => {
    onAdd(sectionId, presetData);
    setOpen(false);
  };

  const handleDeleteUserPreset = async (presetId) => {
    try {
      await axios.delete(`${API}/api/user/element-presets/${presetId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUserPresets(prev => prev.filter(p => p.id !== presetId));
      toast.success('Preset deleted');
    } catch {
      toast.error('Failed to delete preset');
    }
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="w-full flex items-center justify-center gap-1.5 py-2.5 rounded-xl border border-dashed border-gray-300 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400 hover:border-blue-400 hover:text-blue-500 hover:bg-blue-50/50 dark:hover:bg-blue-900/10 transition-all"
        data-testid={`add-element-${sectionId}`}
      >
        <Plus className="w-3.5 h-3.5" /> Add Element
      </button>
    );
  }

  return (
    <div className="rounded-xl border border-blue-200 dark:border-blue-800 bg-white dark:bg-gray-900 overflow-hidden shadow-lg" data-testid={`preset-picker-${sectionId}`}>
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2.5 border-b border-gray-100 dark:border-gray-800">
        <span className="text-xs font-semibold text-gray-800 dark:text-gray-200">Add Element</span>
        <button
          onClick={() => setOpen(false)}
          className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-100 dark:border-gray-800">
        <button
          onClick={() => setTab('system')}
          className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-[11px] font-medium transition-colors ${
            tab === 'system'
              ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-500'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700'
          }`}
          data-testid="presets-tab-system"
        >
          <Star className="w-3 h-3" /> Presets
        </button>
        <button
          onClick={() => setTab('mine')}
          className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-[11px] font-medium transition-colors ${
            tab === 'mine'
              ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-500'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700'
          }`}
          data-testid="presets-tab-mine"
        >
          <Bookmark className="w-3 h-3" /> My Presets
        </button>
      </div>

      {/* Content */}
      <div className="max-h-[380px] overflow-y-auto p-2.5">
        {tab === 'system' && (
          <div className="space-y-4">
            {SYSTEM_PRESETS.map(group => (
              <div key={group.category}>
                <div className="text-[10px] uppercase tracking-[0.14em] text-gray-400 dark:text-gray-500 px-1 mb-2">
                  {group.category}
                </div>
                <div className="space-y-1.5">
                  {group.presets.map(preset => (
                    <PresetCard
                      key={preset.name}
                      preset={preset}
                      onSelect={handleSelect}
                      isUser={false}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === 'mine' && (
          <div>
            {loading ? (
              <div className="text-center py-8 text-xs text-gray-400">Loading...</div>
            ) : userPresets.length === 0 ? (
              <div className="text-center py-8">
                <Bookmark className="w-6 h-6 mx-auto text-gray-300 dark:text-gray-600 mb-2" />
                <p className="text-xs text-gray-500 dark:text-gray-400">No saved presets yet</p>
                <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-1">
                  Use the bookmark icon on any element to save it as a preset
                </p>
              </div>
            ) : (
              <div className="space-y-1.5">
                {userPresets.map(preset => (
                  <PresetCard
                    key={preset.id}
                    preset={preset}
                    onSelect={handleSelect}
                    onDelete={handleDeleteUserPreset}
                    isUser={true}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
